import type { DegreePlan, Schedule } from '@/app/types/interfaces';
import type { GpaPlannerScenarioCourse } from '@/app/services/userAcademicData';
import type { RutgersGpaResult } from '@/app/lib/rutgersGpa';

export type GpaCourseSource = { id: string; label: string; courseIds: string[] };
export function courseBaseId(value: string): string | null {
  const digits = value.replace(/\D/g, '');
  return digits.length === 8 || digits.length === 10 || digits.length === 11 ? digits.slice(0, 8) : null;
}
export function buildGpaCourseSources(schedules: Schedule[], plans: DegreePlan[]): GpaCourseSource[] {
  const ids = (values: string[]) => [...new Set(values.map(courseBaseId).filter((id): id is string => id !== null))];
  return [
    ...schedules.map(schedule => ({ id: `schedule-${schedule.schedule_id}`, label: `Schedule: ${schedule.name}`, courseIds: ids(schedule.classes ?? []) })),
    ...plans.flatMap(plan => {
      let data = plan.plan;
      if (typeof data === 'string') { try { data = JSON.parse(data); } catch { return []; } }
      return (Array.isArray(data?.semesters) ? data.semesters : []).map((semester: { name?: string; courses?: Array<{ kind?: string; code?: string; completed?: boolean }> }, index: number) => ({
        id: `plan-${plan.id}-${index}`, label: `${plan.name}: ${semester.name || `Term ${index + 1}`}`,
        courseIds: ids((semester.courses ?? []).filter(course => !course.completed && (!course.kind || course.kind === 'course')).map(course => course.code ?? '')),
      }));
    }),
  ].filter(source => source.courseIds.length > 0);
}
export function mergeScenarioCourses(existing: GpaPlannerScenarioCourse[], incoming: GpaPlannerScenarioCourse[]): GpaPlannerScenarioCourse[] {
  const seen = new Set(existing.map(course => course.baseId ?? course.id));
  return [...existing, ...incoming.filter(course => { const key = course.baseId ?? course.id; if (seen.has(key)) return false; seen.add(key); return true; })];
}
export function resolveGpaBaseline(record: RutgersGpaResult, gpa?: number, credits?: number, incompleteRecord = false): { gpa: number; credits: number } | null {
  const usableRecord = !incompleteRecord && record.rawGpa !== null && record.gradedCredits > 0 && record.invalidEntries === 0;
  const resolvedGpa = gpa ?? (usableRecord ? record.rawGpa ?? undefined : undefined);
  const resolvedCredits = credits ?? (usableRecord ? record.gradedCredits : undefined);
  return resolvedGpa !== undefined && resolvedCredits !== undefined && Number.isFinite(resolvedGpa) && resolvedGpa >= 0 && resolvedGpa <= 4 && Number.isFinite(resolvedCredits) && resolvedCredits >= 0 ? { gpa: resolvedGpa, credits: resolvedCredits } : null;
}
